import styled, { css } from 'styled-components'
import { theme } from '@notino/react-styleguide'
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react'

// styles
import { BREAKPOINTS, RESERVATION_BOOKING_MODAL_MOBILE_BREAKPOINT } from '../../../styles/constants'
import { NotinoBodySmall, NotinoTitleMedium, NotinoTitleSmall, resetButtonStyles } from '../../../styles/helpers'

// utils
import {
	RESERVATION_BOOKING_MODAL_FIXED_HEADER_HEIGHT,
	RESERVATION_BOOKING_MODAL_LEFT_COL_WIDTH_DESKTOP,
	RESERVATION_BOOKING_MODAL_MAX_WIDTH_DESKTOP,
	RESERVATION_BOOKING_MODAL_RIGHT_COL_WIDTH_DESKTOP
} from '../../../utils/enums'

export const LayoutWrapper = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	max-width: ${RESERVATION_BOOKING_MODAL_MAX_WIDTH_DESKTOP}px;
	overflow: hidden;

	@media (min-width: ${RESERVATION_BOOKING_MODAL_MOBILE_BREAKPOINT}) {
		border-radius: 4px;
	}

	@media (min-width: ${BREAKPOINTS.lg}) {
		flex-direction: row;
	}
`

export const ScrollableContent = styled(OverlayScrollbarsComponent)`
	// crate new layer for fixed position of "FixedHeader" component
	transform: translateZ(0);
	width: 100%;
	flex: 1;
	min-width: 0;
	min-height: 0;
	overflow: auto;
	position: relative;
	z-index: 1;
	transition: height 0.3s ease;
	flex-shrink: 0;

	@media (min-width: ${BREAKPOINTS.lg}) {
		width: ${RESERVATION_BOOKING_MODAL_RIGHT_COL_WIDTH_DESKTOP}px;
	}
`

export const LayoutMain = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
`

const headerFilledStyles = css`
	background-color: ${theme.color.background.primary};
	color: ${theme.color.text.primary};
	border-color: rgba(5, 5, 5, 0.06);
	box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.05);
`

export const FixedHeader = styled.div<{ $isScrolled: boolean }>`
	width: 100%;
	height: ${RESERVATION_BOOKING_MODAL_FIXED_HEADER_HEIGHT}px;
	position: fixed;
	top: 0;
	left: 0;
	z-index: 3;
	transition: all ease 0.3s;
	border-bottom: 1px solid transparent;
	box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0);

	${({ $isScrolled }) =>
		$isScrolled
			? css`
					${headerFilledStyles};
				`
			: css`
					background-color: transparent;
					color: ${theme.color.text.inverse};
				`};

	@media (min-width: ${BREAKPOINTS.lg}) {
		position: sticky;
		${headerFilledStyles};
	}
`

export const FixedHeaderContent = styled.div`
	position: relative;
	padding: 0 50px;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	@media (min-width: ${BREAKPOINTS.lg}) {
		svg {
			color: ${theme.color.text.primary};
		}
	}
`

export const CloseButton = styled.button`
	${resetButtonStyles};
	position: absolute;
	right: 20px;
	top: 50%;
	transform: translateY(-50%);
	@media (min-width: ${BREAKPOINTS.lg}) {
		svg {
			color: ${theme.color.text.primary};
		}
	}
`

export const BackButton = styled.button`
	${resetButtonStyles};
	position: absolute;
	left: 20px;
	top: 50%;
	transform: translateY(-50%);
	@media (min-width: ${BREAKPOINTS.lg}) {
		svg {
			color: ${theme.color.text.primary};
		}
	}
`

export const LayoutHeader = styled.div<{ $backgroundImage?: string }>`
	${({ $backgroundImage }) => css`
		color: ${theme.color.text.inverse};
		width: 100%;
		height: 230px;
		background-color: #9f9f9f; // NOTE: no such color in the notino pallet for background
		background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 100%), url('${$backgroundImage || ''}');
		background-size: cover;
		background-position: center;
		position: relative;
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		padding: 20px;

		@media (min-width: ${BREAKPOINTS.lg}) {
			display: none;
		}
	`}
`

export const LayoutAside = styled.div<{ $backgroundImage?: string }>`
	${({ $backgroundImage }) => css`
		display: none;

		@media (min-width: ${BREAKPOINTS.lg}) {
			color: ${theme.color.text.inverse};
			width: ${RESERVATION_BOOKING_MODAL_LEFT_COL_WIDTH_DESKTOP}px;
			flex-shrink: 0;
			background-color: #9f9f9f; // NOTE: no such color in the notino pallet for background
			background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 100%), url('${$backgroundImage || ''}');
			background-size: cover;
			background-position: center;
			position: relative;
			display: flex;
			flex-direction: column;
			justify-content: flex-end;
			padding: 20px;
		}
	`}
`

export const ContentWrapper = styled.div`
	display: flex;
	width: 100%;
	height: 100%;
`

export const HeaderTitleWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
`

export const HeaderTitleDescription = styled.div`
	${NotinoBodySmall};
`

export const HeaderTitle = styled.h2`
	${NotinoTitleSmall};
	margin: 0;
	outline: none;

	@media (min-width: ${RESERVATION_BOOKING_MODAL_MOBILE_BREAKPOINT}) {
		${NotinoTitleMedium};
	}
`

export const HeaderContent = styled.div``

export const HeaderContentDesktop = styled.div`
	display: none;

	@media (min-width: ${BREAKPOINTS.lg}) {
		display: block;
	}
`

export const LayoutContent = styled.div`
	padding: 20px;

	@media (min-width: ${RESERVATION_BOOKING_MODAL_MOBILE_BREAKPOINT}) {
		padding: 24px;
	}
`

export const LayoutFooter = styled.div`
	flex-shrink: 0;
	border-top: 1px solid rgba(5, 5, 5, 0.06);
	box-shadow: 0px -4px 16px 0px rgba(0, 0, 0, 0.08);
	position: relative;
	z-index: 2;
	padding: 24px;
`
